import { Spacer } from '@ksyos/design-system';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { insurerAddEditSchema } from '@common/validation/insurer';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { IInsurerTurnoverCeiling } from '../../interfaces/insurerTurnoverCeiling';
import {
    AdminTable,
    CancelButton,
    DeleteButton,
    EditButton,
    FormContainer,
    FormDivider,
    Header,
    LinkButton,
    Loading,
    SaveSubmit,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { FormatCurrency } from '../../elements/FormatCurrency';
import { FormContextProvider } from '../../elements/formContext';
import { NumberField } from '../../fields/Number';
import { TextField } from '../../fields/Text';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';
import { InsurerTurnoverCeiling } from './InsurerTurnoverCeiling';

type InsurerAddEditProps = IWithRouterProps;

export interface IInsurer {
    id?: string;
    name: string;
    paymentPeriod: number | null;
    phoneNumberCareMediation: string | null;
    turnoverCeilingEndDate: string | null;
    turnoverCeilingStartDate: string | null;
}

interface IInsurerAddEditState {
    errors: ValidationErrors;
    insurer: IInsurer;
    insurerTurnoverCeilingList: IInsurerTurnoverCeiling[];
    isSaving: boolean;
}

class InnerInsurerAddEdit extends Component<InsurerAddEditProps, IInsurerAddEditState> {
    constructor(props: InsurerAddEditProps) {
        super(props);

        assertHasAuthorization(ManagerAuthorization.insurersAddEdit);

        this.state = {
            insurer: {
                name: '',
                paymentPeriod: null,
                turnoverCeilingStartDate: null,
                turnoverCeilingEndDate: null,
                phoneNumberCareMediation: null,
            },
            isSaving: false,
            errors: emptyValidationErrors,
            insurerTurnoverCeilingList: [],
        };
    }

    public async componentDidMount() {
        if (this.props.router.params.id) {
            this.loadInsurer();
            this.loadInsurerTurnoverCeilingList();
        }
    }

    public render() {
        const actionButtons: JSX.Element[] | undefined = [
            <LinkButton
                icon="add"
                to={'/insurerTurnoverCeiling/add?insurerId=' + this.props.router.params.id}
            >
                {translateKey('insurers.addCeilingYear')}
            </LinkButton>,
        ];

        return (
            <FixedWidthTemplate>
                <Header
                    content={
                        translateKey('insurers.insurer') +
                        ' ' +
                        (this.props.router.params.id ? translateKey('common.edit') : translateKey('common.new'))
                    }
                />

                <Loading
                    item={this.state.insurer}
                    loaded={(insurer) => (
                        <FormContainer onSubmit={async () => await this.save()}>
                            <FormContextProvider
                                value={{
                                    placeholderGroupName: '',
                                    item: this.state.insurer,
                                    errors: this.state.errors,
                                    onChange: async (key, value) => await this.handleChange(key, value),
                                    onDoValidate: async (key) => await this.validateOne(key),
                                }}
                            >
                                <TextField
                                    fieldKey="name"
                                    isRequired
                                    label={translateKey('common.name')}
                                    placeholder={translateKey('common.name')}
                                />

                                <NumberField
                                    fieldKey="paymentPeriod"
                                    label={translateKey('insurers.paymentPeriod')}
                                    placeholder={translateKey('insurers.paymentPeriodPlaceholder')}
                                    tooltipText={translateKey('insurers.paymentPeriodTooltip')}
                                />

                                <FormDivider />
                            </FormContextProvider>

                            <CancelButton
                                disabled={this.state.isSaving}
                                to={this.props.router.searchParams.get('returnPage') || '/insurers'}
                            />
                            <SaveSubmit disabled={this.state.isSaving} />
                        </FormContainer>
                    )}
                />

                {this.props.router.params.id && (
                    <Spacer
                        direction="vertical"
                        spacing="small"
                    >
                        <InsurerTurnoverCeiling
                            handleChange={async (value) => await this.handleChangeTurnoverCeilingInformation(value)}
                            insurer={this.state.insurer}
                        />

                        <Header
                            actionButtons={actionButtons}
                            content={translateKey('insurers.turnoverCeiling')}
                            tooltipText={translateKey('insurers.turnoverCeilingTooltip')}
                        />

                        {this.state.insurerTurnoverCeilingList.length !== 0 && (
                            <AdminTable>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{translateKey('insurers.year')}</TableHeader>
                                        <TableHeader>{translateKey('insurers.ceilingAmount')}</TableHeader>
                                        <TableHeader>{translateKey('insurers.careProductType')}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {this.state.insurerTurnoverCeilingList.map((insurerTurnoverCeiling) => (
                                        <TableRow key={insurerTurnoverCeiling.id}>
                                            <TableData>{insurerTurnoverCeiling.year}</TableData>
                                            <TableData>
                                                <FormatCurrency value={insurerTurnoverCeiling.ceilingAmount} />
                                            </TableData>
                                            <TableData>{insurerTurnoverCeiling.careProductType}</TableData>
                                            <TableData align="right">
                                                <EditButton
                                                    to={
                                                        '/insurerTurnoverCeiling/' +
                                                        insurerTurnoverCeiling.id +
                                                        '?insurerId=' +
                                                        insurerTurnoverCeiling.insurerId
                                                    }
                                                />
                                                <DeleteButton onClick={() => this.remove(insurerTurnoverCeiling.id)} />
                                            </TableData>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </AdminTable>
                        )}
                    </Spacer>
                )}
            </FixedWidthTemplate>
        );
    }

    private async handleChange<K extends keyof IInsurer>(name: K, value: IInsurer[K]) {
        await this.setState(({ insurer }) => ({ insurer: { ...insurer, [name]: value } }));
    }

    private async handleChangeTurnoverCeilingInformation(value: Partial<IInsurer>) {
        await this.setState(({ insurer }) => ({ insurer: { ...insurer, ...value } }));
    }

    private async validateOne(name: keyof IInsurer) {
        await this.setState(({ insurer, errors }) => ({
            errors: updateValidationErrorsForOne(insurerAddEditSchema, insurer, errors, name),
        }));
    }

    private async loadInsurer() {
        const url = '/api/insurers/' + this.props.router.params.id;
        const result = await fetchJson(url, 'GET');
        await this.setState({ insurer: result.json });
    }

    private async loadInsurerTurnoverCeilingList() {
        const url = '/api/insurerTurnoverCeilings?insurerId=' + this.props.router.params.id;
        const result = await fetchJson(url, 'GET');
        await this.setState({ insurerTurnoverCeilingList: result.json.items });
    }

    private async save() {
        let url = '/api/insurers/';
        const returnPage = this.props.router.searchParams.get('returnPage');

        if (this.props.router.params.id) {
            url += this.props.router.params.id;
        }

        const { errors, item } = validate(insurerAddEditSchema, this.state.insurer);
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        await this.setState({ isSaving: true });

        await fetchJson(url, 'POST', item);
        let returnUrl = '/insurers';
        if (returnPage) {
            returnUrl += `?page=${returnPage}`;
        }
        this.props.router.navigate(returnUrl);
    }

    private async remove(id: string) {
        await fetchJson(`/api/insurerTurnoverCeilings/${id}`, 'DELETE');
        await this.loadInsurerTurnoverCeilingList();
    }
}

export const InsurerAddEdit = withRouter(InnerInsurerAddEdit);
